import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { Search, Users, DollarSign, Calendar, Tag, Plus, X, Eye, Mail, Pencil, Send, Trash2, Download, MessageSquare, Wallet } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { BulkDeleteBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '../../components/BulkDelete';
import { EditBookingDialog } from '../../components/EditBookingDialog';
import { TakePaymentDialog } from '../../components/TakePaymentDialog';
import { PaymentModal } from '../../components/PaymentModal';
import { CustomerMessageDialog } from '../../components/CustomerMessageDialog';
import { Pagination, paginate, reconcilePageAfterDelete } from '../../components/Pagination';
import { CustomerProfileDialog } from '../../components/CustomerProfileDialog';
import { CrmListShell, ActionMenu, ResponsiveTable } from '../../components/crm/CrmListShell';
import { AddCustomerDialog } from '../../components/crm/AddCustomerDialog';
import useMessageStream from '../../hooks/useMessageStream';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CrmCustomers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [bulkSelected, setBulkSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_booking');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  // Edit customer info
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [infoForm, setInfoForm] = useState({ customer_name: '', customer_phone: '', child_name: '', child_age: '', address: '', sync_to_bookings: true });
  // Add (manual) customer
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  // Send reminder
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({ booking_id: '', reminder_type: 'email', template: 'party', subject: '', message: '' });
  // Edit booking
  const [editBookingOpen, setEditBookingOpen] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [packages, setPackages] = useState([]);
  // Cancel booking
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelForm, setCancelForm] = useState({ reason: 'customer_request', comment: '', pin: '' });
  const [cancelPreview, setCancelPreview] = useState(null);
  // Take payment
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [payCustomerBookings, setPayCustomerBookings] = useState([]);
  const [payLoading, setPayLoading] = useState(false);
  // Standalone POS charge (any amount, with/without a customer)
  const [posOpen, setPosOpen] = useState(false);
  const [posPreset, setPosPreset] = useState(null);
  const openPos = (customer = null) => { setPosPreset(customer); setPosOpen(true); };

  // Send message (email/SMS)
  const [msgDialogOpen, setMsgDialogOpen] = useState(false);
  const [msgTargetCustomer, setMsgTargetCustomer] = useState(null);
  const [msgDefaultChannel, setMsgDefaultChannel] = useState('email');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Delete one customer (per-row)
  const [deleteCustomerOpen, setDeleteCustomerOpen] = useState(false);
  const [deleteCustomerTarget, setDeleteCustomerTarget] = useState(null);
  const [deleteCustomerSubmitting, setDeleteCustomerSubmitting] = useState(false);

  const openMessageDialog = (customer, channel = 'email') => {
    setMsgTargetCustomer({ name: customer.name, email: customer.email, phone: customer.phone });
    setMsgDefaultChannel(channel);
    setMsgDialogOpen(true);
  };

  const openPayForCustomer = async (customer) => {
    setPayLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers/${encodeURIComponent(customer.email)}/outstanding`,
        { withCredentials: true });
      const items = (res.data?.items || []).map((b) => ({
        ...b,
        customer_name: customer.name,
        customer_email: customer.email,
      }));
      if (items.length === 0) {
        // No outstanding booking → offer a standalone walk-in charge instead
        openPos({ email: customer.email, name: customer.name, phone: customer.phone, customer_id: customer.customer_id });
      } else {
        setPayCustomerBookings(items);
        setPayDialogOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load outstanding bookings');
    } finally { setPayLoading(false); }
  };

  useEffect(() => { fetchCustomers(); }, [sortBy]);
  // Quick-action: autofocus search when ?focus=search is in URL.
  // Deep-link: open a specific customer profile when ?email=<addr> is in URL
  // (used by the prospect "Manage" button for converted prospects).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('focus') === 'search') {
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select?.();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
    const emailParam = params.get('email');
    if (emailParam) {
      openDetail(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
  useEffect(() => {
    (async () => {
      try {
        const [e, p] = await Promise.all([
          axios.get(`${API_URL}/api/admin/employees`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/packages`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setEmployees(Array.isArray(e.data) ? e.data : (e.data?.employees || []));
        setPackages(p.data || []);
      } catch { /* silent */ }
    })();
  }, []);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers?sort_by=${sortBy}`, { withCredentials: true });
      setCustomers(res.data);
    } catch (e) { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  };

  // Live cross-tab refresh: any admin/manager deleting a customer/booking
  // in another tab fires a `crm_changed` event — we refetch so this tab
  // mirrors the change instantly. We also refetch on booking changes
  // because customers are aggregated from bookings.
  useMessageStream((event) => {
    if (!event || event.type !== 'crm_changed') return;
    if (['customers', 'bookings'].includes(event.section)) {
      fetchCustomers();
    }
  });

  const deleteCustomer = async () => {
    if (!deleteCustomerTarget) return;
    setDeleteCustomerSubmitting(true);
    try {
      const res = await axios.delete(
        `${API_URL}/api/admin/customers/${encodeURIComponent(deleteCustomerTarget.email)}`,
        { withCredentials: true },
      );
      const cascaded = res.data?.cascade?.bookings || 0;
      toast.success(
        `Deleted ${deleteCustomerTarget.name || deleteCustomerTarget.email}`
        + (cascaded > 0 ? ` (+${cascaded} booking${cascaded === 1 ? '' : 's'} cancelled)` : '')
      );
      setDeleteCustomerOpen(false);
      setDeleteCustomerTarget(null);
      // Optimistic local removal so the page shifts back if this was the last
      // row on the last page before the SSE refetch lands.
      setCustomers((prev) => {
        const next = (prev || []).filter((c) => c.email !== deleteCustomerTarget.email);
        setPage((p) => reconcilePageAfterDelete(next.length, p, pageSize));
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete customer');
    } finally {
      setDeleteCustomerSubmitting(false);
    }
  };

  const openDetail = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/customers/${encodeURIComponent(email)}`, { withCredentials: true });
      setSelectedCustomer(res.data);
      setNoteInput(res.data.notes || '');
      setDetailOpen(true);
    } catch (e) { toast.error('Failed to load customer details'); }
  };

  const addTag = async () => {
    if (!tagInput.trim() || !selectedCustomer) return;
    const newTags = [...(selectedCustomer.tags || []), tagInput.trim()];
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(selectedCustomer.email)}`,
        { tags: newTags }, { withCredentials: true });
      setSelectedCustomer(prev => ({ ...prev, tags: newTags }));
      setTagInput('');
      toast.success('Tag added');
    } catch (e) { toast.error('Failed to add tag'); }
  };

  const removeTag = async (tag) => {
    if (!selectedCustomer) return;
    const newTags = selectedCustomer.tags.filter(t => t !== tag);
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(selectedCustomer.email)}`,
        { tags: newTags }, { withCredentials: true });
      setSelectedCustomer(prev => ({ ...prev, tags: newTags }));
      toast.success('Tag removed');
    } catch (e) { toast.error('Failed'); }
  };

  const saveNotes = async () => {
    if (!selectedCustomer) return;
    try {
      await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(selectedCustomer.email)}`,
        { notes: noteInput }, { withCredentials: true });
      setSelectedCustomer(prev => ({ ...prev, notes: noteInput }));
      toast.success('Notes saved');
    } catch (e) { toast.error('Failed'); }
  };

  const openEditInfo = () => {
    if (!selectedCustomer) return;
    const latest = selectedCustomer.bookings?.[0] || {};
    setInfoForm({
      customer_name: selectedCustomer.name || latest.customer_name || '',
      customer_phone: selectedCustomer.phone || latest.customer_phone || '',
      child_name: latest.child_name || '',
      child_age: latest.child_age || '',
      address: latest.address || '',
      sync_to_bookings: true,
    });
    setEditInfoOpen(true);
  };
  const saveCustomerInfo = async () => {
    if (!infoForm.customer_name.trim()) { toast.error('Name required'); return; }
    try {
      const payload = {
        customer_name: infoForm.customer_name.trim(),
        customer_phone: infoForm.customer_phone.trim(),
        child_name: infoForm.child_name.trim(),
        child_age: infoForm.child_age ? parseInt(infoForm.child_age, 10) : undefined,
        address: infoForm.address.trim(),
        sync_to_bookings: infoForm.sync_to_bookings,
      };
      const res = await axios.patch(`${API_URL}/api/admin/customers/${encodeURIComponent(selectedCustomer.email)}`,
        payload, { withCredentials: true });
      toast.success(`Saved · ${res.data.bookings_synced} booking(s) updated`);
      setEditInfoOpen(false);
      await openDetail(selectedCustomer.email);
      fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openReminder = (bookingId = '') => {
    setReminderForm({
      booking_id: bookingId || selectedCustomer?.bookings?.[0]?.booking_id || '',
      reminder_type: 'email', template: 'party', subject: '', message: '',
    });
    setReminderOpen(true);
  };
  const sendReminder = async () => {
    if (!reminderForm.booking_id) { toast.error('Select a booking'); return; }
    if (reminderForm.template === 'custom' && !reminderForm.message.trim()) { toast.error('Message required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/reminders/send`, {
        booking_id: reminderForm.booking_id,
        reminder_type: reminderForm.reminder_type,
        template: reminderForm.template,
        subject: reminderForm.subject.trim() || null,
        message: reminderForm.message.trim() || null,
      }, { withCredentials: true });
      toast.success('Reminder sent');
      setReminderOpen(false);
      await openDetail(selectedCustomer.email);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEditBooking = async (b) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/bookings/${b.booking_id}`, { withCredentials: true });
      setEditBooking(res.data?.booking || b);
    } catch {
      setEditBooking(b);
    }
    setEditBookingOpen(true);
  };

  const openCancelBooking = async (b) => {
    setCancelBooking(b);
    setCancelForm({ reason: 'customer_request', comment: '', pin: '' });
    try {
      const r = await axios.get(`${API_URL}/api/admin/bookings/${b.booking_id}/cancel-preview`, { withCredentials: true });
      setCancelPreview(r.data);
    } catch { setCancelPreview(null); }
    setCancelOpen(true);
  };
  const submitCancel = async () => {
    if (!/^\d{4}$/.test(cancelForm.pin)) { toast.error('4-digit PIN required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/bookings/${cancelBooking.booking_id}/cancel`, cancelForm, { withCredentials: true });
      toast.success('Booking cancelled');
      setCancelOpen(false);
      await openDetail(selectedCustomer.email);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const exportCustomerCSV = () => {
    if (!selectedCustomer) return;
    const rows = [
      ['Field', 'Value'],
      ['Name', selectedCustomer.name],
      ['Email', selectedCustomer.email],
      ['Phone', selectedCustomer.phone],
      ['Total Bookings', selectedCustomer.total_bookings],
      ['Total Spent', `$${(selectedCustomer.total_spent || 0).toFixed(2)}`],
      [],
      ['Booking ID', 'Date', 'Time', 'Package', 'Child', 'Guests', 'Total', 'Status'],
      ...(selectedCustomer.bookings || []).map(b => [
        b.booking_id, b.date, b.time_slot, b.package_name, b.child_name, b.guest_count,
        `$${(b.total || 0).toFixed(2)}`, b.status,
      ]),
    ];
    const csv = rows.map(r => Array.isArray(r) ? r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',') : '').join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `customer_${selectedCustomer.email.replace(/[^a-z0-9]/gi, '_')}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const filtered = customers.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase()) ||
    c.phone?.includes(search)
  );
  // Reset to page 1 whenever the search/sort changes so the user always
  // sees the head of the matching list.
  useEffect(() => { setPage(1); }, [search, sortBy, pageSize]);
  const pagedCustomers = paginate(filtered, page, pageSize);

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <CrmListShell
        testid="crm-customers"
        title="Customers"
        subtitle={`${customers.length} total`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              onClick={() => openPos(null)}
              variant="outline"
              className="rounded-full border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FFEDF3] shadow-sm"
              data-testid="take-payment-btn"
            >
              <Wallet className="w-4 h-4 mr-1" /> Take Payment
            </Button>
            <Button
              onClick={() => setAddCustomerOpen(true)}
              className="rounded-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white shadow-sm"
              data-testid="add-customer-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Customer
            </Button>
          </div>
        }
        search={
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B6B7A]" />
            <Input
              ref={searchInputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, phone…"
              className="pl-10 rounded-xl border-[#E8E5DF] h-9"
              data-testid="customer-search"
            />
          </div>
        }
        filters={
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-44 rounded-xl border-[#E8E5DF] h-9 text-sm" data-testid="customer-sort">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last_booking">Recent Activity</SelectItem>
              <SelectItem value="total_spent">Top Spenders</SelectItem>
              <SelectItem value="total_bookings">Most Bookings</SelectItem>
            </SelectContent>
          </Select>
        }
      >
        <ResponsiveTable
          testid="customers"
          getKey={(c) => c.email}
          empty="No customers found"
          rows={pagedCustomers}
          columns={[
            {
              key: 'name', header: 'Customer',
              render: (c) => (
                <button
                  type="button"
                  onClick={() => openDetail(c.email)}
                  className="text-left -mx-2 -my-1 px-2 py-1 rounded-lg transition hover:bg-[#FFFDF8]"
                  data-testid={`customer-name-btn-${c.email}`}
                >
                  <div className="font-medium text-[#1E1E24] hover:text-[#FF3B6B] flex items-center gap-1.5">
                    <span className="truncate max-w-[180px]">{c.name}</span>
                    {c.is_one_time && (
                      <span className="text-[9px] py-0 px-1.5 bg-orange-50 text-orange-700 border border-orange-200 rounded uppercase font-semibold" data-testid={`onetime-badge-${c.email}`}>
                        ONE-TIME
                      </span>
                    )}
                    {c.is_manual && !c.is_one_time && (
                      <span className="text-[9px] py-0 px-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded uppercase font-semibold" data-testid={`manual-badge-${c.email}`}>
                        MANUAL
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B6B7A] truncate max-w-[200px]">{c.email}</p>
                </button>
              ),
            },
            { key: 'phone', header: 'Phone', hideOnMobile: true, render: (c) => <span className="text-sm">{c.phone || '—'}</span> },
            {
              key: 'last_booking', header: 'Last Booking Date', hideOnMobile: true,
              render: (c) => <span className="text-xs text-[#6B6B7A]">{c.last_booking ? new Date(c.last_booking).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</span>,
            },
            {
              key: 'actions', header: 'View Profile', align: 'right',
              render: (c) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => openDetail(c.email)}
                    className="rounded-full h-8 px-3 text-xs border-[#FF3B6B]/30 text-[#FF3B6B] hover:bg-[#FF3B6B] hover:text-white"
                    data-testid={`view-customer-${c.email}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" /> View Profile
                  </Button>
                  <ActionMenu
                    testid={`customer-actions-${c.email}`}
                    items={[
                      { label: 'Send email', icon: Mail, onClick: () => openMessageDialog(c, 'email'), disabled: !c.email, testid: `email-customer-${c.email}` },
                      { label: 'Send SMS', icon: MessageSquare, onClick: () => openMessageDialog(c, 'sms'), disabled: !c.phone, testid: `sms-customer-${c.email}` },
                      { label: 'Take payment', icon: Wallet, onClick: () => openPos({ email: c.email, name: c.name, phone: c.phone, customer_id: c.customer_id }), testid: `pay-customer-${c.email}` },
                      { separator: true },
                      { label: 'Delete', icon: Trash2, destructive: true, onClick: () => { setDeleteCustomerTarget(c); setDeleteCustomerOpen(true); }, testid: `delete-customer-${c.email}` },
                    ]}
                  />
                </div>
              ),
            },
          ]}
        />
      </CrmListShell>

      <Pagination
        page={page}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        testidPrefix="customers-pagination"
        itemLabel="customer"
      />

      {/* Detail Dialog — 7-tab Customer Profile (Iteration 86) */}
      <CustomerProfileDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        customer={selectedCustomer}
        onRefresh={(email) => { if (email) openDetail(email); }}
        onOpenEditBooking={openEditBooking}
        onOpenReminder={openReminder}
        onOpenCancelBooking={openCancelBooking}
        onOpenMessageDialog={openMessageDialog}
        onOpenPayDialog={openPayForCustomer}
        onOpenNewBooking={(cust) => {
          navigate(`/book?email=${encodeURIComponent(cust.email)}`);
        }}
        onOpenCreateInvitation={(cust) => {
          navigate(`/crm/invitations?prefill_email=${encodeURIComponent(cust.email)}`);
        }}
      />

      {/* Edit Customer Info Dialog */}
      <Dialog open={editInfoOpen} onOpenChange={setEditInfoOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Edit Customer Info</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Name</Label>
              <Input value={infoForm.customer_name} onChange={e => setInfoForm(p => ({ ...p, customer_name: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-info-name" />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Phone</Label>
              <Input value={infoForm.customer_phone} onChange={e => setInfoForm(p => ({ ...p, customer_phone: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-info-phone" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B6B7A]">Child Name</Label>
                <Input value={infoForm.child_name} onChange={e => setInfoForm(p => ({ ...p, child_name: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-info-child" />
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Child Age</Label>
                <Input type="number" min="0" max="20" value={infoForm.child_age} onChange={e => setInfoForm(p => ({ ...p, child_age: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-info-child-age" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#6B6B7A]">Address</Label>
              <Input value={infoForm.address} onChange={e => setInfoForm(p => ({ ...p, address: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="edit-info-address" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={infoForm.sync_to_bookings} onCheckedChange={(v) => setInfoForm(p => ({ ...p, sync_to_bookings: !!v }))} data-testid="edit-info-sync" />
              Sync these changes to all existing bookings
            </label>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditInfoOpen(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button onClick={saveCustomerInfo} className="flex-1 bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="save-customer-info">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }}>Send Reminder</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#6B6B7A]">Booking</Label>
              <Select value={reminderForm.booking_id} onValueChange={(v) => setReminderForm(p => ({ ...p, booking_id: v }))}>
                <SelectTrigger className="mt-1 bg-white" data-testid="reminder-booking"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {(selectedCustomer?.bookings || []).filter(b => b.status !== 'cancelled').map(b => (
                    <SelectItem key={b.booking_id} value={b.booking_id}>{b.child_name}'s party · {b.date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#6B6B7A]">Channel</Label>
                <Select value={reminderForm.reminder_type} onValueChange={(v) => setReminderForm(p => ({ ...p, reminder_type: v }))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="reminder-channel"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Template</Label>
                <Select value={reminderForm.template} onValueChange={(v) => setReminderForm(p => ({ ...p, template: v }))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="reminder-template"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="party">Party reminder</SelectItem>
                    <SelectItem value="payment">Payment reminder</SelectItem>
                    <SelectItem value="thankyou">Thank you</SelectItem>
                    <SelectItem value="custom">Custom message</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {reminderForm.template === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Subject (email only)</Label>
                  <Input value={reminderForm.subject} onChange={e => setReminderForm(p => ({ ...p, subject: e.target.value }))} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="reminder-subject" />
                </div>
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Message</Label>
                  <Textarea value={reminderForm.message} onChange={e => setReminderForm(p => ({ ...p, message: e.target.value }))} rows={4}
                    placeholder="Use {name}, {child_name}, {date}, {time}, {package}, {balance}" className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="reminder-message" />
                </div>
              </>
            )}
            <div className="bg-[#FFFDF8] rounded-xl p-3 border border-[#E8E5DF] text-xs text-[#6B6B7A]">
              Will be sent via <strong>{reminderForm.reminder_type}</strong> to {selectedCustomer?.email}
              {reminderForm.reminder_type !== 'email' && ` / ${selectedCustomer?.phone}`}
            </div>
            <Button onClick={sendReminder} className="w-full bg-[#06D6A0] hover:bg-[#05B88A] text-white rounded-full" data-testid="send-reminder-submit">
              <Send className="w-4 h-4 mr-2" /> Send Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Booking Dialog (shared component) */}
      <EditBookingDialog
        booking={editBooking}
        open={editBookingOpen}
        onClose={() => setEditBookingOpen(false)}
        onSaved={() => {
          setEditBookingOpen(false);
          if (selectedCustomer?.email) openDetail(selectedCustomer.email);
        }}
        packages={packages}
        employees={employees}
      />

      {/* Cancel Booking Dialog */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Fredoka' }} className="text-red-600">Cancel Booking</DialogTitle></DialogHeader>
          {cancelBooking && (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-xl p-3 border border-red-100 text-sm">
                <p className="font-semibold">{cancelBooking.child_name}'s party · {cancelBooking.date}</p>
                {cancelPreview && (
                  <p className="text-xs mt-1 text-red-700">
                    Refund: <strong>${cancelPreview.refund_amount?.toFixed(2)}</strong> ({cancelPreview.refund_pct}%) · {cancelPreview.hours_to_party?.toFixed(1)}h to party
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs text-[#6B6B7A]">Reason</Label>
                <Select value={cancelForm.reason} onValueChange={v => setCancelForm(p => ({ ...p, reason: v }))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="cancel-reason"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="customer_request">Customer request</SelectItem>
                    <SelectItem value="weather">Weather</SelectItem>
                    <SelectItem value="staff_shortage">Staff shortage</SelectItem>
                    <SelectItem value="venue_issue">Venue issue</SelectItem>
                    <SelectItem value="no_show">No-show (50% refund)</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {cancelForm.reason === 'other' && (
                <div>
                  <Label className="text-xs text-[#6B6B7A]">Comment</Label>
                  <Textarea value={cancelForm.comment} onChange={e => setCancelForm(p => ({ ...p, comment: e.target.value }))} rows={2} className="mt-1 rounded-xl border-[#E8E5DF]" data-testid="cancel-comment" />
                </div>
              )}
              <div>
                <Label className="text-xs text-[#6B6B7A]">Employee 4-digit PIN *</Label>
                <Input type="password" maxLength={4} inputMode="numeric" value={cancelForm.pin} onChange={e => setCancelForm(p => ({ ...p, pin: e.target.value.replace(/\D/g, '') }))} className="mt-1 rounded-xl border-red-200" data-testid="cancel-pin" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCancelOpen(false)} className="flex-1 rounded-full">Back</Button>
                <Button onClick={submitCancel} className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="confirm-cancel">Confirm Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BulkDeleteBar
        section="customers"
        selectedIds={bulkSelected}
        onClearSelection={() => setBulkSelected([])}
        onDone={() => fetchCustomers()}
        labelSingular="customer"
        dataTestPrefix="customers-bulk"
      />

      {/* Take payment from a customer's outstanding bookings */}
      <TakePaymentDialog
        open={payDialogOpen}
        onOpenChange={setPayDialogOpen}
        bookings={payCustomerBookings}
        onSuccess={() => fetchCustomers()}
      />

      {/* Standalone POS card charge — any amount, optional customer link */}
      <PaymentModal
        open={posOpen}
        onOpenChange={setPosOpen}
        mode="admin"
        posMode
        presetCustomer={posPreset}
        title="Take Payment"
        onSuccess={() => fetchCustomers()}
      />

      {/* Send Email / SMS to customer */}
      <CustomerMessageDialog
        open={msgDialogOpen}
        onOpenChange={setMsgDialogOpen}
        customer={msgTargetCustomer}
        defaultChannel={msgDefaultChannel}
        onSent={() => { /* optionally refresh; no-op for now */ }}
      />

      {/* Add Customer (manual) Dialog */}
      <AddCustomerDialog
        open={addCustomerOpen}
        onOpenChange={setAddCustomerOpen}
        onCreated={() => fetchCustomers()}
        onResolveExisting={(email) => {
          // Used for both "Open existing" (active) and after reactivating a
          // deleted customer. Refresh the list from the server AND surface the
          // customer immediately (filter to their email) so a reactivated record
          // isn't buried by the last-booking sort, then open their profile.
          setAddCustomerOpen(false);
          setSearch(email);
          fetchCustomers();
          openDetail(email);
        }}
      />
      {/* Delete one customer */}
      <Dialog open={deleteCustomerOpen} onOpenChange={(o) => { if (!deleteCustomerSubmitting) setDeleteCustomerOpen(o); }}>
        <DialogContent className="max-w-md bg-white" data-testid="delete-customer-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2" style={{ fontFamily: 'Fredoka' }}>
              <Trash2 className="w-5 h-5" /> Delete customer?
            </DialogTitle>
            <DialogDescription>
              {deleteCustomerTarget?.total_bookings > 0
                ? `This will permanently delete ${deleteCustomerTarget?.name || deleteCustomerTarget?.email} AND cancel all ${deleteCustomerTarget?.total_bookings} of their bookings (refunds are NOT processed). Slots will be released.`
                : `This will remove ${deleteCustomerTarget?.name || deleteCustomerTarget?.email} from the customer list.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteCustomerOpen(false)} className="rounded-full" disabled={deleteCustomerSubmitting} data-testid="delete-customer-cancel">
              Cancel
            </Button>
            <Button
              onClick={deleteCustomer}
              disabled={deleteCustomerSubmitting}
              className="rounded-full bg-red-500 hover:bg-red-600 text-white"
              data-testid="delete-customer-confirm"
            >
              {deleteCustomerSubmitting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmCustomers;
